"use client";

import { useUserPlan } from "@/lib/hook/use-user-plan";
import { usePathname } from "@/i18n/routing";
import { isAdFreePath, hasAdsRuntime } from "@/lib/ads/policy";
import { useUser } from "@clerk/nextjs";
import { createContext, useContext, useEffect } from "react";

const AdsContext = createContext({ shouldShowAds: false, isPro: false });

export function AdsProvider({ children }: { children: React.ReactNode }) {
  const { user } = useUser();
  const { data, isLoaded, isLoggedIn } = useUserPlan();

  const pathname = usePathname();
  const adFree = data?.id === user?.id && data?.adFree === true;

  useEffect(() => {
    // Auto Ads cannot be unloaded after execution. A fresh document removes
    // its runtime when a visitor becomes entitled to ad-free browsing.
    if (adFree && hasAdsRuntime(document)) window.location.reload();
  }, [adFree]);

  const shouldShowAds =
    !isAdFreePath(pathname) &&
    isLoaded &&
    (!isLoggedIn || (data?.id === user?.id && data?.adFree === false));

  return (
    <AdsContext.Provider
      value={{
        shouldShowAds,
        isPro: isLoaded && data?.id === user?.id && data?.plan === "PRO",
      }}
    >
      {children}
    </AdsContext.Provider>
  );
}

export const useShouldShowAds = () => useContext(AdsContext).shouldShowAds;
export const useIsPro = () => useContext(AdsContext).isPro;
